// client/src/firebase.js
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

// 1) Config Firebase via env vars
const firebaseConfig = {
  apiKey:             process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:         process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:          process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:      process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:  process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:              process.env.REACT_APP_FIREBASE_APP_ID,
};

// 2) Init Firebase
const firebaseApp = !getApps().length
  ? initializeApp(firebaseConfig)
  : getApps()[0];

// 3) Auth
export const auth = getAuth(firebaseApp);

// Auth secondaire (utilisé pour l’association de comptes sans impacter la session courante)
let secondaryApp;
try {
  secondaryApp = getApp('AccountLinker');
} catch {
  secondaryApp = initializeApp(firebaseConfig, 'AccountLinker');
}
export const secondaryAuth = getAuth(secondaryApp);
setPersistence(secondaryAuth, inMemoryPersistence).catch(() => {});

// 4) Firestore & Storage
export const firestore = getFirestore(firebaseApp);
export const db = firestore;

const rawBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'ecobill-pay.firebasestorage.app';
const normalizedBucket = rawBucket.replace(/^gs:\/\//, '');

// Certains environnements (prod vs dev) peuvent référencer l'ancien bucket appspot
// ou le nouveau domaine firebasestorage.app. On prépare donc un bucket de secours
// pour éviter les 404 en lecture si les fichiers résident sur l'autre bucket.
const altBucket = normalizedBucket.endsWith('.firebasestorage.app')
  ? normalizedBucket.replace(/\.firebasestorage\.app$/, '.appspot.com')
  : normalizedBucket.replace(/\.appspot\.com$/, '.firebasestorage.app');

export const storage = getStorage(firebaseApp, `gs://${normalizedBucket}`);
export const storageFallback = altBucket && altBucket !== normalizedBucket
  ? getStorage(firebaseApp, `gs://${altBucket}`)
  : null;

/**
 * getDownloadURL avec secours automatique sur le bucket alternatif
 * (utile quand les fichiers historiques sont dans l'autre bucket).
 */
export async function getDownloadURLSmart(pathOrRef) {
  const primaryRef = typeof pathOrRef === 'string' ? storageRef(storage, pathOrRef) : pathOrRef;
  try {
    return await getDownloadURL(primaryRef);
  } catch (err) {
    const code = err?.code || '';
    const retryable = code.includes('object-not-found') || code.includes('unauthorized') || code.includes('not-found');
    if (storageFallback && retryable) {
      try {
        const fallbackRef = typeof pathOrRef === 'string'
          ? storageRef(storageFallback, pathOrRef)
          : storageRef(storageFallback, primaryRef.fullPath || '');
        return await getDownloadURL(fallbackRef);
      } catch (err2) {
        console.warn('[firebase] getDownloadURL fallback failed:', err2);
      }
    }
    throw err;
  }
}

// 5) Base URL pour les endpoints (prod = même origin + rewrite /api/**, dev = serveur local)
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5099');

// 5bis) CSRF helper (Cloud Functions protègent /api/sessionLogin & /api/sessionLogout)
function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie?.split(';').map(c => c.trim()).find(c => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

async function ensureCsrfToken() {
  let token = readCookie('XSRF-TOKEN');
  if (token) return token;
  try {
    await fetch(`${API_BASE}/api/csrf-token`, { credentials: 'include' });
    token = readCookie('XSRF-TOKEN');
  } catch (err) {
    console.error('[firebase.js] Impossible de récupérer le CSRF token:', err);
  }
  return token;
}

// 6) Variables d’état
let sessionRefreshInterval = null;
const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000;
let isLoggingIn = false;
let lastLoginTs = 0;
const LOGIN_THROTTLE_MS = 30 * 1000;
const LOGOUT_GUARD_KEY = 'ecobill_logout_guard';

function clearLogoutGuard() {
  try { localStorage.removeItem(LOGOUT_GUARD_KEY); } catch {}
}

// Renforcements anti-déconnexion
const SESSION_REFRESH_SOFT_MS = 3.5 * 24 * 60 * 60 * 1000;
const SESSION_LOGIN_TS_KEY = 'lastSessionLoginAt';

function nowMs() { return Date.now(); }
function getLastSessionLoginAt() {
  try { return parseInt(localStorage.getItem(SESSION_LOGIN_TS_KEY) || '0', 10); } catch { return 0; }
}
function markSessionLoginSuccess() {
  try { localStorage.setItem(SESSION_LOGIN_TS_KEY, String(nowMs())); } catch {}
}
function needsSoftRefresh() {
  const last = getLastSessionLoginAt();
  if (!last) return true;
  return (nowMs() - last) > SESSION_REFRESH_SOFT_MS;
}

// Retry
async function attemptSessionLogin(idToken, retries = 3) {
  try {
    const csrfToken = await ensureCsrfToken();
    const res = await fetch(`${API_BASE}/api/sessionLogin`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
      },
      body: JSON.stringify({ idToken })
    });

    if (res.ok) { markSessionLoginSuccess(); return true; }

    if (res.status === 429 && retries > 0) {
      console.warn(`[firebase.js] 429 reçu, retry dans 10s (${retries} restants)`);
      await new Promise(r => setTimeout(r, 10000));
      return attemptSessionLogin(idToken, retries - 1);
    }

    console.error('[firebase.js] sessionLogin failed', res.status, await res.text());
    return false;
  } catch (err) {
    console.error('[firebase.js] Fetch sessionLogin error:', err);
    return false;
  }
}

// Refresh proactif
async function ensureFreshSession(reason = 'unknown') {
  if (process.env.NODE_ENV !== 'production') return;
  const user = auth.currentUser;
  if (!user) return;
  if (!needsSoftRefresh()) return;

  const now = Date.now();
  if (now - lastLoginTs < LOGIN_THROTTLE_MS) return;
  lastLoginTs = now;
  if (isLoggingIn) return;
  isLoggingIn = true;

  try {
    const idToken = await user.getIdToken();
    const ok = await attemptSessionLogin(idToken);
    if (ok) console.log(`[firebase.js] 🔄 sessionLogin proactif (${reason}) OK`);
  } catch (e) {
    console.error('[firebase.js] ensureFreshSession error:', e);
  } finally {
    isLoggingIn = false;
  }
}

// Listener auth <-> cookie session
function attachAuthStateListener() {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[firebase.js] ⚙️ Auth middleware désactivé en dev');
    return;
  }

  console.log('[firebase.js] 🔐 Attache onAuthStateChanged (prod)');
  onAuthStateChanged(auth, async (user) => {
    // LOGIN
    if (user) {
      const now = Date.now();
      if (now - lastLoginTs < LOGIN_THROTTLE_MS) {
        console.warn('[firebase.js] ⏳ sessionLogin throttled');
        return;
      }
      lastLoginTs = now;
      if (isLoggingIn) return;
      isLoggingIn = true;

      try {
        const idToken = await user.getIdToken();
      const ok = await attemptSessionLogin(idToken);
      if (ok) {
        console.log('[firebase.js] ✅ Session cookie créée');
        try {
          localStorage.setItem('userEmail', user.email || '');
          localStorage.setItem('ecobill_last_uid', user.uid);
          clearLogoutGuard();
        } catch {}
      }
      } catch (e) {
        console.error('[firebase.js] sessionLogin global error:', e);
      } finally {
        isLoggingIn = false;
      }

      if (sessionRefreshInterval) { clearInterval(sessionRefreshInterval); sessionRefreshInterval = null; }
      sessionRefreshInterval = setInterval(async () => {
        try {
          if (!auth.currentUser) return;
          const freshToken = await auth.currentUser.getIdToken(true);
          await attemptSessionLogin(freshToken);
          console.log('[firebase.js] 🔁 sessionLogin refresh OK');
        } catch (e) {
          console.error('[firebase.js] session refresh error:', e);
        }
      }, FOUR_DAYS_MS);

      const onFocus = () => ensureFreshSession('focus');
      const onVisible = () => { if (document.visibilityState === 'visible') ensureFreshSession('visibility'); };
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisible);

      const unsubscribeIdToken = onIdTokenChanged(auth, (u) => { if (u) ensureFreshSession('onIdTokenChanged'); });

      (auth)._ecobillCleanupHandlers?.();
      (auth)._ecobillCleanupHandlers = () => {
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVisible);
        unsubscribeIdToken && unsubscribeIdToken();
      };

    // LOGOUT
    } else {
      console.log('[firebase.js] ⚠️ Déconnexion détectée, nettoyage local');
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}

      if (sessionRefreshInterval) { clearInterval(sessionRefreshInterval); sessionRefreshInterval = null; }

      (auth)._ecobillCleanupHandlers?.();
      (auth)._ecobillCleanupHandlers = null;

      try {
        const csrfToken = await ensureCsrfToken();
        await fetch(`${API_BASE}/api/sessionLogout`, {
          method: 'POST',
          credentials: 'include',
          headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {}
        });
        console.log('[firebase.js] 🗑️ Session cookie supprimé');
      } catch (e) {
        console.error('[firebase.js] sessionLogout error:', e);
      }
    }
  });
}

// Persistance + attach listener
export const persistenceReady = setPersistence(auth, indexedDBLocalPersistence)
  .catch(() => setPersistence(auth, browserLocalPersistence))
  .catch(() => setPersistence(auth, inMemoryPersistence))
  .then(() => { attachAuthStateListener(); })
  .catch(err => {
    console.error('[firebase.js] Auth persistence error (fallback attaching listener):', err);
    attachAuthStateListener();
  });

// Export pour reset de mot de passe
export { sendPasswordResetEmail };
